// Asset Inventory: vault-first view of monitor_assets populated by the monitor
// beacon's ARP discovery + fingerprint pipeline. The landing is a grid of vault
// cards (one per monitor beacon's vault label); clicking a card drills into that
// vault's assets table. Row click opens a side drawer with services + findings.
// Blue-team only surface; fully decoupled from the pentest side.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Brain,
  Bug,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  Database,
  Flame,
  Hash,
  Loader2,
  Network,
  Radar,
  RefreshCw,
  Rocket,
  ScanLine,
  Server,
  ShieldCheck,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import { monitorAPI } from '../../services/api';
import { useUrlPatch, useUrlState } from '../../hooks/useUrlState';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../common/PageHeader';
import ConfirmModal from '../common/ConfirmModal';
import DeployBeaconModal from '../Beacons/DeployBeaconModal';
import { formatRelativeTime, formatAbsoluteDate } from '../../utils/time';
import NetworkMap from './NetworkMap';

const LAST_SEEN_OPTIONS = [
  { label: 'Any time', value: '' },
  { label: 'Last hour', value: 1 },
  { label: 'Last 24 hours', value: 24 },
  { label: 'Last 7 days', value: 168 },
  { label: 'Last 30 days', value: 720 },
];

const PAGE_SIZE = 50;
const UNASSIGNED = '__unassigned__';

const SEVERITY_BADGE = {
  critical: 'bg-red-100 text-red-700 border border-red-300',
  high: 'bg-orange-100 text-orange-700 border border-orange-300',
  medium: 'bg-yellow-100 text-yellow-700 border border-yellow-300',
  low: 'bg-blue-100 text-blue-700 border border-blue-300',
  info: 'bg-slate-100 text-slate-700 border border-slate-300',
};

const SOURCE_BADGE = {
  nvd: 'bg-purple-100 text-purple-700 border border-purple-300',
  nuclei: 'bg-cyan-100 text-cyan-700 border border-cyan-300',
  zap: 'bg-emerald-100 text-emerald-700 border border-emerald-300',
};

const EOL_BADGE = {
  eol: 'bg-red-100 text-red-700 border border-red-300',
  approaching: 'bg-amber-100 text-amber-700 border border-amber-300',
  ok: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  unknown: 'bg-slate-100 text-slate-500 border border-slate-200',
};

const EOL_FILTER_OPTIONS = [
  { label: 'Any EOL status', value: '' },
  { label: 'EOL', value: 'eol' },
  { label: 'Approaching', value: 'approaching' },
  { label: 'OK', value: 'ok' },
  { label: 'Unknown', value: 'unknown' },
];

function EolCell({ status, date }) {
  const s = status || 'unknown';
  const label = s === 'eol' ? (date || 'EOL') : s === 'approaching' ? `~ ${date || 'soon'}` : s === 'ok' ? 'OK' : '—';
  return <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${EOL_BADGE[s]}`}>{label}</span>;
}

// Rank for sorting findings worst-first (critical at the top).
const SEVERITY_ORDER = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };

// Max service pills rendered inline before collapsing the rest into a +N chip.
const PILL_LIMIT = 4;

// IPv4 -> sortable integer; falls back to a string-stable value otherwise so
// the comparator never throws on an unexpected address shape.
function ipSortKey(ip) {
  const parts = String(ip || '').split('.');
  if (parts.length === 4) {
    return parts.reduce((acc, oct) => acc * 256 + (parseInt(oct, 10) || 0), 0);
  }
  return Number.MAX_SAFE_INTEGER;
}

const SORT_ACCESSORS = {
  ip: (a) => ipSortKey(a.ip),
  services: (a) => a.services_count || 0,
  findings: (a) => a.open_findings_count || 0,
  last_seen: (a) => new Date(a.last_seen_at).getTime() || 0,
};

// ---- vault status derivation (shared by card + drill-down header) ----

function vaultStatus(v) {
  if (v.critical_count > 0) {
    return { tone: 'critical', dot: 'bg-red-500', ring: 'border-red-300', glow: 'shadow-[0_1px_3px_rgba(239,68,68,0.25)]' };
  }
  if (v.high_count > 0) {
    return { tone: 'high', dot: 'bg-orange-500', ring: 'border-orange-300', glow: 'shadow-[0_1px_3px_rgba(249,115,22,0.25)]' };
  }
  return { tone: 'clean', dot: 'bg-shinobi-green', ring: 'border-slate-200', glow: '' };
}

export default function AssetInventory() {
  // Which vault is open lives in the URL, so a refresh lands back on the vault
  // you were looking at instead of the landing grid. `value` is enough to
  // rebuild the pair: the label is either the vault name or the Unassigned
  // bucket's display name.
  const [vaultParam, setVaultParam] = useUrlState('vault', '');
  const patchUrl = useUrlPatch();
  const selectedVault = vaultParam
    ? { value: vaultParam, label: vaultParam === UNASSIGNED ? 'Unassigned' : vaultParam }
    : null;
  const openVault = (v) => setVaultParam(v.value);
  // One patch, not a set plus a reset: separate calls in the same handler
  // overwrite each other, and the vault would survive the clear.
  const closeVault = () => patchUrl({
    vault: null, view: null, mesh: null, lens: null,
    pivots: null, seg: null, group: null, estate: null,
  });
  // null = closed; { vault } prefills the Deploy modal for a monitor beacon.
  const [deploy, setDeploy] = useState(null);

  const openDeploy = (vaultName = '') => setDeploy({ vault: vaultName || '' });

  return (
    <>
      {selectedVault ? (
        <VaultAssets
          vault={selectedVault}
          onBack={closeVault}
          onDeploy={openDeploy}
        />
      ) : (
        <VaultLanding onOpen={openVault} onDeploy={openDeploy} />
      )}
      <DeployBeaconModal
        isOpen={!!deploy}
        onClose={() => setDeploy(null)}
        initialVault={deploy?.vault || ''}
        initialProfile="internal_scanner"
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Vault landing: grid of cards
// ---------------------------------------------------------------------------

function VaultLanding({ onOpen, onDeploy }) {
  const { isOperator } = useAuth();
  const [vaults, setVaults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [confirmVault, setConfirmVault] = useState(null); // vault summary obj
  const [deleting, setDeleting] = useState(false);

  const fetchVaults = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await monitorAPI.listVaults();
      setVaults(resp.data.vaults || []);
      setError(null);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVaults();
  }, [fetchVaults]);

  const handleDeleteVault = async () => {
    if (!confirmVault) return;
    setDeleting(true);
    try {
      const value = confirmVault.vault === null ? UNASSIGNED : confirmVault.vault;
      await monitorAPI.deleteVault(value);
      setConfirmVault(null);
      await fetchVaults();
    } catch (err) {
      console.error('Failed to delete vault:', err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Asset Inventory"
        subtitle="Monitoring vaults discovered by network monitor beacons. Open a vault to view its hosts and services."
        icon={Server}
        accent="green"
        lastRefresh={lastRefresh?.toLocaleTimeString()}
        actions={
          <div className="flex items-center gap-2">
            {isOperator && (
              <button
                onClick={() => onDeploy('')}
                className="px-3 py-1.5 text-xs rounded border border-wasabi-300 bg-wasabi-100 text-wasabi-700 hover:bg-wasabi-100 transition-colors flex items-center gap-1.5"
                title="Deploy a monitor beacon"
              >
                <Rocket className="w-3.5 h-3.5" />
                Deploy beacon
              </button>
            )}
            <button
              onClick={fetchVaults}
              className="px-3 py-1.5 text-xs rounded border border-shinobi-purple/40 bg-shinobi-purple/10 text-shinobi-purple hover:bg-shinobi-purple/20 transition-colors flex items-center gap-1.5"
              title="Refresh"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Refresh
            </button>
          </div>
        }
      />

      <div className="px-6">
        {loading ? (
          <div className="cyber-card p-8 text-slate-500 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading vaults...
          </div>
        ) : error ? (
          <div className="cyber-card p-8 text-red-600 text-sm">Error: {error}</div>
        ) : vaults.length === 0 ? (
          <VaultEmptyState onDeploy={isOperator ? () => onDeploy('') : undefined} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {vaults.map((v) => (
              <VaultCard
                key={v.vault || UNASSIGNED}
                vault={v}
                onClick={() =>
                  onOpen({
                    value: v.vault === null ? UNASSIGNED : v.vault,
                    label: v.vault || 'Unassigned',
                  })
                }
                onDelete={isOperator ? () => setConfirmVault(v) : undefined}
              />
            ))}
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={!!confirmVault}
        onClose={() => (deleting ? null : setConfirmVault(null))}
        onConfirm={handleDeleteVault}
        title="Delete vault"
        message={
          confirmVault
            ? `Delete "${confirmVault.vault || 'Unassigned'}" and all ${confirmVault.total_assets} of its assets (plus their services and findings)? ${
                confirmVault.vault
                  ? 'Its monitor beacon stays installed but is unassigned; a running beacon will re-discover hosts under Unassigned until reassigned or destroyed.'
                  : ''
              } This cannot be undone.`
            : ''
        }
        confirmText={deleting ? 'Deleting…' : 'Delete vault'}
        variant="danger"
      />
    </div>
  );
}

function VaultCard({ vault, onClick, onDelete }) {
  const status = vaultStatus(vault);
  const online = vault.beacons_online > 0;
  const name = vault.vault || 'Unassigned';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
      className={`cyber-card text-left p-5 rounded-lg border ${status.ring} ${status.glow} hover:border-wasabi-300 transition-all group cursor-pointer`}
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-slate-900 truncate group-hover:text-shinobi-green transition-colors">
            {name}
          </h3>
          <div className="flex items-center gap-1.5 mt-1 text-xs text-gray-500">
            <span className={`w-1.5 h-1.5 rounded-full ${online ? 'bg-shinobi-green' : 'bg-gray-600'}`} />
            {online
              ? `${vault.beacons_online} beacon${vault.beacons_online === 1 ? '' : 's'} online`
              : 'beacon offline'}
            {vault.last_heartbeat && (
              <span className="text-gray-600"> · {formatRelativeTime(vault.last_heartbeat)}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 mt-0.5">
          <span className={`w-2.5 h-2.5 rounded-full ${status.dot}`} title={status.tone} />
          {onDelete && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="text-gray-600 hover:text-danger-red transition-colors opacity-0 group-hover:opacity-100"
              title="Delete vault"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="flex items-end justify-between">
        <div>
          <div className="text-3xl font-bold text-slate-900 leading-none">{vault.total_assets}</div>
          <div className="text-xs text-gray-500 mt-1">assets</div>
        </div>
        <div className="text-right">
          {vault.vulnerable_hosts > 0 ? (
            <div
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                status.tone === 'critical'
                  ? 'bg-red-100 text-red-700 border border-red-300'
                  : 'bg-orange-100 text-orange-700 border border-orange-300'
              }`}
            >
              <AlertTriangle className="w-3.5 h-3.5" />
              {vault.vulnerable_hosts} vulnerable host{vault.vulnerable_hosts === 1 ? '' : 's'}
            </div>
          ) : (
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-wasabi-100 text-wasabi-700 border border-shinobi-green/25">
              <ShieldCheck className="w-3.5 h-3.5" />
              clean
            </div>
          )}
          {vault.total_open_findings > 0 && (
            <div className="text-[11px] text-gray-500 mt-1.5">
              {vault.total_open_findings} open finding{vault.total_open_findings === 1 ? '' : 's'}
              {vault.critical_count > 0 && <span className="text-red-600"> · {vault.critical_count} crit</span>}
              {vault.high_count > 0 && <span className="text-orange-600"> · {vault.high_count} high</span>}
            </div>
          )}
          {vault.eol_count > 0 && (
            <div className="text-[11px] text-amber-600 mt-1.5 inline-flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> {vault.eol_count} EOL
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function VaultEmptyState({ onDeploy }) {
  return (
    <div className="cyber-card p-10 text-center">
      <Network className="w-12 h-12 mx-auto mb-3 text-gray-600" />
      <p className="text-slate-700 text-sm mb-1">No monitoring vaults yet.</p>
      <p className="text-gray-500 text-xs max-w-lg mx-auto">
        Deploy a monitor beacon (Internal Scanner profile) with a vault label, e.g.
        <span className="font-mono text-slate-500"> --vault "Hillsboro (US)"</span>. Each beacon's
        vault appears here as a card once it discovers hosts on its subnet.
      </p>
      {onDeploy && (
        <button
          onClick={onDeploy}
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-xs rounded border border-wasabi-300 bg-wasabi-100 text-wasabi-700 hover:bg-wasabi-100 transition-colors"
        >
          <Rocket className="w-3.5 h-3.5" />
          Deploy a monitor beacon
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Vault drill-down: the assets table, filtered to one vault
// ---------------------------------------------------------------------------

function VaultAssets({ vault, onBack, onDeploy }) {
  const { isOperator } = useAuth();
  const [assets, setAssets] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filterCidr, setFilterCidr] = useState('');
  const [filterLastSeen, setFilterLastSeen] = useState('');
  const [eolFilter, setEolFilter] = useState('');
  const [cidrInput, setCidrInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [drawerAsset, setDrawerAsset] = useState(null);
  const [sort, setSort] = useState({ key: 'findings', dir: 'desc' });
  const [selected, setSelected] = useState(() => new Set());
  const [confirmDelete, setConfirmDelete] = useState(null); // { ids, label }
  const [deleting, setDeleting] = useState(false);
  const [scanning, setScanning] = useState(null); // 'nuclei' | 'zap' | null
  const [scanMsg, setScanMsg] = useState(null); // { tone, text }
  const [vaultStatus, setVaultStatus] = useState(null); // vault summary w/ monitor_status
  // Table or map survives a refresh for the same reason the vault does.
  const [viewMode, setViewMode] = useUrlState('view', 'table');
  const [identifying, setIdentifying] = useState(false);
  const [identifyMsg, setIdentifyMsg] = useState(null); // { tone, text }
  const [probing, setProbing] = useState(false);
  const [probeMsg, setProbeMsg] = useState(null); // { tone, text }

  // Poll the vault summary (incl. live monitor_status) for the phase pipeline.
  useEffect(() => {
    let cancelled = false;
    const isUnassigned = vault.value === UNASSIGNED;
    const match = (v) => (isUnassigned ? v.vault === null : v.vault === vault.value);
    const poll = async () => {
      try {
        const resp = await monitorAPI.listVaults();
        if (!cancelled) {
          setVaultStatus((resp.data.vaults || []).find(match) || null);
        }
      } catch {
        /* leave last-known status */
      }
    };
    poll();
    const id = setInterval(poll, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [vault.value]);

  // Bulk AI identity for the vault. Progress is read off the vault summary the
  // effect above already polls every 5s, so there is nothing to track here
  // beyond the dispatch itself.
  const runIdentify = async () => {
    setIdentifying(true);
    setIdentifyMsg(null);
    try {
      const resp = await monitorAPI.identifyVault(vault.value);
      const n = resp.data.dispatched;
      setIdentifyMsg(
        n === 0
          ? { tone: 'ok', text: 'Every host in this vault is already profiled.' }
          : { tone: 'ok', text: `Profiling ${n} host${n === 1 ? '' : 's'}. Results appear as each one completes.` },
      );
    } catch (err) {
      setIdentifyMsg({ tone: 'err', text: err?.response?.data?.detail || err.message || 'Identify failed' });
    } finally {
      setIdentifying(false);
    }
  };

  // Vault-wide reachability probe. Like identify, there is no job to track:
  // results land as beacon reports and the 5s vault poll above picks up the
  // coverage counts as they arrive.
  const runProbe = async () => {
    setProbing(true);
    setProbeMsg(null);
    try {
      const resp = await monitorAPI.probeReachability(vault.value);
      const { vantage_points: vp, targets } = resp.data;
      setProbeMsg({
        tone: 'ok',
        text: `Probing ${targets} host${targets === 1 ? '' : 's'} from ${vp} beacon${vp === 1 ? '' : 's'}. Results appear as each probe finishes.`,
      });
    } catch (err) {
      setProbeMsg({ tone: 'err', text: err?.response?.data?.detail || err.message || 'Probe dispatch failed' });
    } finally {
      setProbing(false);
    }
  };

  const runScan = async (engine) => {
    setScanning(engine);
    setScanMsg(null);
    try {
      const resp = await monitorAPI.scanVault(vault.value, engine);
      const n = resp.data.targets;
      setScanMsg({
        tone: 'ok',
        text: `${engine === 'nuclei' ? 'Nuclei' : 'ZAP'} scan started on ${n} target${n === 1 ? '' : 's'}. Findings appear here as scans complete.`,
      });
    } catch (err) {
      setScanMsg({
        tone: 'err',
        text: err?.response?.data?.detail || err.message || 'Scan dispatch failed',
      });
    } finally {
      setScanning(null);
    }
  };

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        skip: page * PAGE_SIZE,
        limit: PAGE_SIZE,
        vault: vault.value,
      };
      if (filterCidr) params.cidr = filterCidr;
      if (filterLastSeen) params.last_seen_hours = filterLastSeen;
      const resp = await monitorAPI.listAssets(params);
      setAssets(resp.data.assets || []);
      setTotal(resp.data.total || 0);
      setSelected(new Set());
      setError(null);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [page, filterCidr, filterLastSeen, vault.value]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allOnPageSelected = assets.length > 0 && assets.every((a) => selected.has(a.id));
  const toggleSelectAll = () => {
    setSelected((prev) => {
      if (assets.every((a) => prev.has(a.id))) return new Set();
      return new Set(assets.map((a) => a.id));
    });
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      const ids = confirmDelete.ids;
      if (ids.length === 1) await monitorAPI.deleteAsset(ids[0]);
      else await monitorAPI.bulkDeleteAssets(ids);
      setConfirmDelete(null);
      await fetchAssets();
    } catch (err) {
      console.error('Failed to delete asset(s):', err);
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const applyCidr = (e) => {
    e.preventDefault();
    setPage(0);
    setFilterCidr(cidrInput.trim());
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const toggleSort = (key) => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'ip' ? 'asc' : 'desc' }
    );
  };

  const sortedAssets = useMemo(() => {
    const accessor = SORT_ACCESSORS[sort.key] || SORT_ACCESSORS.services;
    const factor = sort.dir === 'asc' ? 1 : -1;
    const filtered = eolFilter ? assets.filter((a) => a.eol_status === eolFilter) : assets;
    return [...filtered].sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      if (av < bv) return -1 * factor;
      if (av > bv) return 1 * factor;
      return ipSortKey(a.ip) - ipSortKey(b.ip);
    });
  }, [assets, sort, eolFilter]);

  return (
    <div className="space-y-6">
      <PageHeader
        title={vault.label}
        subtitle="Hosts and services discovered in this vault via ARP sweep + fingerprint."
        icon={Server}
        accent="green"
        lastRefresh={lastRefresh?.toLocaleTimeString()}
        actions={
          <div className="flex items-center gap-2">
            {isOperator && (
              <button
                onClick={() => onDeploy(vault.value === UNASSIGNED ? '' : vault.label)}
                className="px-3 py-1.5 text-xs rounded border border-wasabi-300 bg-wasabi-100 text-wasabi-700 hover:bg-wasabi-100 transition-colors flex items-center gap-1.5"
                title="Deploy a monitor beacon into this vault"
              >
                <Rocket className="w-3.5 h-3.5" />
                Deploy beacon
              </button>
            )}
            <button
              onClick={fetchAssets}
              className="px-3 py-1.5 text-xs rounded border border-shinobi-purple/40 bg-shinobi-purple/10 text-shinobi-purple hover:bg-shinobi-purple/20 transition-colors flex items-center gap-1.5"
              title="Refresh"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Refresh
            </button>
          </div>
        }
      />

      <div className="px-6 space-y-4">
        {/* One bordered strip owns the breadcrumb, the vault counts and the
            view switch. Previously the back link and the switch floated on a
            bare line belonging to nothing, which read as two unrelated
            controls that happened to collide. */}
        <div className="cyber-card flex items-center gap-3 px-3.5 py-2 flex-wrap">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-1 text-[13px] text-slate-500 hover:text-shinobi-green transition-colors"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            All vaults
          </button>
          <span className="text-[11px] text-slate-400">
            <span className="font-semibold text-slate-600">{vault.label}</span>
            {vaultStatus ? (
              <>
                {' '}&middot; {vaultStatus.total_assets ?? 0} host{(vaultStatus.total_assets ?? 0) === 1 ? '' : 's'}
                {vaultStatus.total_open_findings ? ` · ${vaultStatus.total_open_findings} open finding${vaultStatus.total_open_findings === 1 ? '' : 's'}` : ''}
                {vaultStatus.eol_count ? ` · ${vaultStatus.eol_count} EOL` : ''}
              </>
            ) : null}
          </span>

          <div role="tablist" aria-label="Asset view"
            className="ml-auto inline-flex items-center gap-0.5 rounded-lg bg-slate-100 p-0.5 text-xs">
            {[['table', 'Table', Database], ['map', 'Map', Network]].map(([k, label, Icon]) => (
              <button key={k} onClick={() => setViewMode(k)}
                role="tab" aria-selected={viewMode === k}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-all ${viewMode === k ? 'bg-white text-wasabi-700 font-semibold shadow-sm ring-1 ring-slate-900/5' : 'text-slate-500 hover:text-slate-800'}`}>
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* AI identity progress. A one-line strip rather than a card, and
            deliberately outside the map-mode collapse below: the device glyphs
            on the map are driven by this, so "why are there no icons?" has to
            be answerable from the map view itself. */}
        <IdentityStrip
          status={vaultStatus}
          isOperator={isOperator}
          busy={identifying}
          message={identifyMsg}
          onRun={runIdentify}
        />

        {/* Reachability coverage sits beside identity for the same reason:
            what the map can say about lateral movement depends on it, so the
            answer to "why does the map say unmeasured?" has to be on screen in
            both views. */}
        <ReachabilityStrip
          status={vaultStatus}
          isOperator={isOperator}
          busy={probing}
          message={probeMsg}
          onRun={runProbe}
        />

        {/* The pipeline, triage and scan cards stack to well over half the
            viewport, which left the map a leftover strip. In map mode they
            collapse so the board gets the height it needs; the map's own
            toolbar keeps the discovering indicator, and its intel rail carries
            the same counts. */}
        {viewMode === 'table' && (
          <>
            {/* Live phase pipeline */}
            <PhaseStepper status={vaultStatus} />

            {/* Wasabi Shinobi AI risk triage */}
            <AIRiskTriage vaultKey={vault.value} vaultLabel={vault.label} />
          </>
        )}

        {/* Scan controls (operators only; Wasabian is read-only) */}
        {viewMode === 'table' && isOperator && (
          <div className="cyber-card p-4 flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-slate-800">Vulnerability scanning</h3>
              <p className="text-xs text-gray-500">
                Run nuclei or ZAP against this vault's HTTP services on demand.
              </p>
              {scanMsg && (
                <p className={`text-xs mt-1.5 ${scanMsg.tone === 'ok' ? 'text-shinobi-green' : 'text-danger-red'}`}>
                  {scanMsg.text}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => runScan('nuclei')}
                disabled={!!scanning}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs bg-wasabi-100 text-wasabi-700 border border-wasabi-300 hover:bg-wasabi-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {scanning === 'nuclei' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bug className="w-3.5 h-3.5" />}
                Run Nuclei
              </button>
              <button
                onClick={() => runScan('zap')}
                disabled={!!scanning}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs bg-shinobi-purple/10 text-shinobi-purple border border-shinobi-purple/30 hover:bg-shinobi-purple/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {scanning === 'zap' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Radar className="w-3.5 h-3.5" />}
                Run ZAP
              </button>
            </div>
          </div>
        )}

        {viewMode === 'table' ? (
        <>
        {/* Filters */}
        <div className="cyber-card p-4">
          <div className="flex flex-wrap items-end gap-3">
            <form onSubmit={applyCidr} className="flex-1 min-w-[180px]">
              <label className="text-xs text-slate-500 mb-1 block">CIDR</label>
              <input
                type="text"
                value={cidrInput}
                onChange={(e) => setCidrInput(e.target.value)}
                placeholder="e.g. 10.0.0.0/24"
                className="w-full bg-cyber-300 border border-slate-200 rounded px-3 py-1.5 text-sm text-slate-900 focus:outline-none focus:border-shinobi-purple"
              />
            </form>
            <div className="flex-1 min-w-[160px]">
              <label className="text-xs text-slate-500 mb-1 block">Last seen</label>
              <select
                value={filterLastSeen}
                onChange={(e) => {
                  setPage(0);
                  setFilterLastSeen(e.target.value);
                }}
                className="w-full bg-cyber-300 border border-slate-200 rounded px-3 py-1.5 text-sm text-slate-900 focus:outline-none focus:border-shinobi-purple"
              >
                {LAST_SEEN_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div className="flex-1 min-w-[160px]">
              <label className="text-xs text-slate-500 mb-1 block">EOL status</label>
              <select
                value={eolFilter}
                onChange={(e) => setEolFilter(e.target.value)}
                className="w-full bg-cyber-300 border border-slate-200 rounded px-3 py-1.5 text-sm text-slate-900 focus:outline-none focus:border-shinobi-purple"
              >
                {EOL_FILTER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Bulk action bar (operators only) */}
        {isOperator && selected.size > 0 && (
          <div className="flex items-center justify-between cyber-card px-4 py-2.5 border border-danger-red/30 bg-danger-red/5">
            <span className="text-sm text-slate-700">{selected.size} selected</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSelected(new Set())}
                className="text-xs text-slate-500 hover:text-slate-800 transition-colors px-2 py-1"
              >
                Clear
              </button>
              <button
                onClick={() => setConfirmDelete({ ids: Array.from(selected), label: `${selected.size} assets` })}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded bg-danger-red/15 text-danger-red border border-danger-red/40 hover:bg-danger-red/25 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Delete selected
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="cyber-card overflow-hidden">
          {loading ? (
            <div className="p-8 text-slate-500 flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading assets...
            </div>
          ) : error ? (
            <div className="p-8 text-red-600 text-sm">Error: {error}</div>
          ) : assets.length === 0 ? (
            <AssetsEmptyState />
          ) : (
            <>
              <table className="w-full">
                <thead className="bg-cyber-200/40 border-b border-slate-200">
                  <tr>
                    <th className="w-10 px-3 py-3">
                      <input
                        type="checkbox"
                        checked={allOnPageSelected}
                        onChange={toggleSelectAll}
                        className="accent-shinobi-green cursor-pointer"
                        title="Select all on this page"
                      />
                    </th>
                    <SortHeader label="IP" sortKey="ip" sort={sort} onSort={toggleSort} />
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">MAC</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Hostname</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">OS</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">EOL</th>
                    <SortHeader label="Services" sortKey="services" sort={sort} onSort={toggleSort} />
                    <SortHeader label="Open findings" sortKey="findings" sort={sort} onSort={toggleSort} align="right" />
                    <SortHeader label="Last seen" sortKey="last_seen" sort={sort} onSort={toggleSort} />
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody>
                  {sortedAssets.map((a) => (
                    <tr
                      key={a.id}
                      onClick={() => setDrawerAsset(a)}
                      className={`border-t border-slate-200 hover:bg-cyber-200/30 transition-colors cursor-pointer ${selected.has(a.id) ? 'bg-shinobi-green/5' : ''}`}
                    >
                      <td className="w-10 px-3 py-3 align-top" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selected.has(a.id)}
                          onChange={() => toggleSelect(a.id)}
                          className="accent-shinobi-green cursor-pointer mt-0.5"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm font-mono text-slate-900 align-top whitespace-nowrap">{a.ip}</td>
                      <td className="px-4 py-3 text-sm font-mono text-slate-500 align-top whitespace-nowrap">{a.mac || '—'}</td>
                      <td className="px-4 py-3 text-sm text-slate-700 align-top">{a.hostname || <span className="text-gray-600">—</span>}</td>
                      <td className="px-4 py-3 text-sm text-slate-700 align-top whitespace-nowrap">
                        {a.os_name ? `${a.os_name}${a.os_version ? ' ' + a.os_version : ''}` : <span className="text-gray-600">—</span>}
                      </td>
                      <td className="px-4 py-3 align-top"><EolCell status={a.eol_status} date={a.eol_date} /></td>
                      <td className="px-4 py-3 align-top">
                        <ServicePills services={a.services_preview} count={a.services_count} />
                      </td>
                      <td className="px-4 py-3 text-sm font-mono text-right align-top">
                        {a.open_findings_count > 0 ? (
                          <span className="text-orange-700">{a.open_findings_count}</span>
                        ) : (
                          <span className="text-gray-500">0</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500 align-top whitespace-nowrap" title={formatAbsoluteDate(a.last_seen_at)}>
                        {formatRelativeTime(a.last_seen_at)}
                      </td>
                      <td className="px-2 align-top">
                        <div className="flex items-center gap-1">
                          {isOperator && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setConfirmDelete({ ids: [a.id], label: a.ip }); }}
                              className="text-gray-600 hover:text-danger-red transition-colors p-1"
                              title="Delete asset"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <ChevronRight className="w-4 h-4 text-gray-600" />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {total > PAGE_SIZE && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 text-xs text-slate-500">
                  <span>
                    Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setPage(Math.max(0, page - 1))}
                      disabled={page === 0}
                      className="px-2 py-1 rounded border border-slate-200 hover:bg-cyber-200/40 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Prev
                    </button>
                    <span className="px-2">Page {page + 1} / {totalPages}</span>
                    <button
                      onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                      disabled={page >= totalPages - 1}
                      className="px-2 py-1 rounded border border-slate-200 hover:bg-cyber-200/40 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        </>
        ) : (
          <NetworkMap vault={vault.value} onOpenAsset={setDrawerAsset} liveStatus={vaultStatus} />
        )}
      </div>

      {drawerAsset && (
        <AssetDrawer assetId={drawerAsset.id} onClose={() => setDrawerAsset(null)} />
      )}

      <ConfirmModal
        isOpen={!!confirmDelete}
        onClose={() => (deleting ? null : setConfirmDelete(null))}
        onConfirm={handleDelete}
        title={confirmDelete && confirmDelete.ids.length > 1 ? 'Delete assets' : 'Delete asset'}
        message={
          confirmDelete
            ? `Delete ${confirmDelete.label} (including all discovered services and findings)? This cannot be undone.`
            : ''
        }
        confirmText={deleting ? 'Deleting…' : 'Delete'}
        variant="danger"
      />
    </div>
  );
}

// One-line "N of M hosts profiled" strip with the vault-wide identify action.
// Before this existed, profiling a freshly discovered vault meant opening every
// host's drawer and clicking Identify one at a time.
function IdentityStrip({ status, isOperator, busy, message, onRun }) {
  if (!status) return null;
  const total = status.total_assets ?? 0;
  if (total === 0) return null;
  const done = status.identified_count ?? 0;
  const remaining = Math.max(0, total - done);
  const pct = total > 0 ? Math.round((100 * done) / total) : 0;
  const complete = remaining === 0;

  return (
    <div className="flex items-center gap-3 flex-wrap rounded-lg border border-shinobi-purple/25 bg-shinobi-purple/5 px-3 py-2">
      <span className="inline-flex items-center gap-1.5 text-shinobi-purple shrink-0">
        <Brain className="w-3.5 h-3.5" />
        <span className="text-[10px] uppercase tracking-[0.18em] font-semibold">Device identity</span>
      </span>
      <span className="text-xs text-slate-700">
        <span className="font-semibold">{done}</span> of {total} host{total === 1 ? '' : 's'} profiled by Wasabi Shinobi AI
      </span>
      <div className="h-1.5 w-24 rounded-full bg-slate-200 overflow-hidden shrink-0" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <div className={`h-full rounded-full transition-all ${complete ? 'bg-shinobi-green' : 'bg-shinobi-purple'}`} style={{ width: `${pct}%` }} />
      </div>
      {message && (
        <span className={`text-[11px] ${message.tone === 'ok' ? 'text-shinobi-green' : 'text-danger-red'}`}>
          {message.text}
        </span>
      )}
      {isOperator && !complete && (
        <button
          onClick={onRun}
          disabled={busy}
          className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] rounded border border-shinobi-purple/40 bg-shinobi-purple/10 text-shinobi-purple hover:bg-shinobi-purple/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          title="Infer role, criticality and posture for every unprofiled host in this vault"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
          Identify {remaining} host{remaining === 1 ? '' : 's'}
        </button>
      )}
      {complete && (
        <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-shinobi-green">
          <ShieldCheck className="w-3 h-3" /> Complete
        </span>
      )}
    </div>
  );
}

// "N of M hosts probed" strip with the vault-wide reachability action.
//
// The wording is careful in one specific way: one vantage point mostly
// restates the fingerprint, because the service scan already runs from that
// same beacon. Two beacons disagreeing about a host is the result worth
// having, so the strip says so when there is only one.
function ReachabilityStrip({ status, isOperator, busy, message, onRun }) {
  if (!status) return null;
  const total = status.total_assets ?? 0;
  if (total === 0) return null;
  const done = status.reach_measured_hosts ?? 0;
  const vantage = status.reach_vantage_points ?? 0;
  const pct = total > 0 ? Math.round((100 * Math.min(done, total)) / total) : 0;
  const checked = status.reach_last_checked ? new Date(status.reach_last_checked) : null;
  const canRun = isOperator && (status.beacons_online ?? 0) > 0;

  return (
    <div className="flex items-center gap-3 flex-wrap rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
      <span className="inline-flex items-center gap-1.5 text-slate-600 shrink-0">
        <Network className="w-3.5 h-3.5" />
        <span className="text-[10px] uppercase tracking-[0.18em] font-semibold">Reachability</span>
      </span>
      {done === 0 ? (
        <span className="text-xs text-slate-600">
          No path in this vault has been probed yet, so what each host accepts is unmeasured rather than closed.
        </span>
      ) : (
        <>
          <span className="text-xs text-slate-700">
            <span className="font-semibold">{done}</span> of {total} host{total === 1 ? '' : 's'} probed
            {' '}from {vantage} vantage point{vantage === 1 ? '' : 's'}
          </span>
          <div className="h-1.5 w-24 rounded-full bg-slate-200 overflow-hidden shrink-0" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
            <div className="h-full rounded-full bg-slate-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
          {checked && (
            <span className="text-[10px] text-slate-400" title={checked.toISOString()}>
              last probed {checked.toLocaleString()}
            </span>
          )}
        </>
      )}
      {done > 0 && vantage < 2 && (
        <span className="text-[11px] text-amber-700">
          One vantage point mostly restates the service scan. A second beacon in this vault is what reveals per-source firewall rules.
        </span>
      )}
      {message && (
        <span className={`text-[11px] ${message.tone === 'ok' ? 'text-shinobi-green' : 'text-danger-red'}`}>
          {message.text}
        </span>
      )}
      {canRun && (
        <button
          onClick={onRun}
          disabled={busy}
          className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] rounded border border-slate-300 bg-white text-slate-600 hover:text-slate-900 hover:border-slate-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          title="TCP connect to every inventoried host in this vault from every monitor beacon in it"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Radar className="w-3 h-3" />}
          {done === 0 ? 'Probe reachability' : 'Re-probe'}
        </button>
      )}
    </div>
  );
}

// Tenable-style phase pipeline derived from the vault's live monitor_status
// (beacon-reported) with graceful fallback to the summary counts.
function PhaseStepper({ status }) {
  if (!status) return null;
  const ms = status.monitor_status || null;
  const hostsFound = ms?.hosts_found ?? status.total_assets ?? 0;
  const fingerprinted = ms ? (ms.hosts_fingerprinted ?? 0) : hostsFound;
  const services = ms?.services_found ?? 0;
  const activeScans = ms?.active_scans ?? 0;
  const discovering = ms?.discovering ?? false;
  const findings = status.total_open_findings ?? 0;

  const fpPct = hostsFound > 0 ? Math.round((100 * Math.min(fingerprinted, hostsFound)) / hostsFound) : 0;
  const live = discovering || activeScans > 0 || (hostsFound > 0 && fingerprinted < hostsFound);

  const phases = [
    {
      key: 'discovery', label: 'Discovery', icon: Radar,
      state: discovering ? 'active' : hostsFound > 0 ? 'done' : 'pending',
      metric: `${hostsFound} host${hostsFound === 1 ? '' : 's'}`,
    },
    {
      key: 'fingerprint', label: 'Fingerprint', icon: ScanLine,
      state: hostsFound > 0 && fingerprinted < hostsFound ? 'active' : fingerprinted > 0 ? 'done' : 'pending',
      metric: hostsFound > 0 ? `${Math.min(fingerprinted, hostsFound)}/${hostsFound}` : '',
      pct: fpPct,
    },
    {
      key: 'nvd', label: 'NVD enrich', icon: Database,
      state: findings > 0 ? 'done' : services > 0 ? 'active' : 'pending',
      metric: `${findings} CVE${findings === 1 ? '' : 's'}`,
    },
    {
      key: 'scan', label: 'Vuln scan', icon: Bug,
      state: activeScans > 0 ? 'active' : findings > 0 ? 'done' : 'pending',
      metric: activeScans > 0 ? `${activeScans} running` : '',
    },
  ];

  const toneFor = (s) =>
    s === 'active'
      ? 'border-wasabi-300 text-shinobi-green bg-wasabi-100'
      : s === 'done'
      ? 'border-slate-200 text-slate-700 bg-cyber-200/30'
      : 'border-slate-200 text-gray-600 bg-transparent';

  return (
    <div className="cyber-card p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
          Scan pipeline
          {live && (
            <span className="inline-flex items-center gap-1 text-[11px] text-shinobi-green">
              <span className="w-1.5 h-1.5 rounded-full bg-shinobi-green animate-pulse" />
              Live
            </span>
          )}
        </h3>
        {!live && hostsFound > 0 && <span className="text-[11px] text-gray-500">idle</span>}
      </div>
      <div className="flex items-stretch gap-2 overflow-x-auto">
        {phases.map((p, i) => {
          const Icon = p.icon;
          return (
            <div key={p.key} className="flex items-center gap-2 flex-1 min-w-[140px]">
              <div className={`flex-1 rounded-lg border px-3 py-2 transition-colors ${toneFor(p.state)}`}>
                <div className="flex items-center gap-1.5 text-xs font-medium">
                  {p.state === 'active' ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : p.state === 'done' ? (
                    <ShieldCheck className="w-3.5 h-3.5" />
                  ) : (
                    <Icon className="w-3.5 h-3.5" />
                  )}
                  {p.label}
                </div>
                {p.metric && <div className="text-[11px] mt-1 opacity-80 font-mono">{p.metric}</div>}
                {p.key === 'fingerprint' && p.state !== 'pending' && (
                  <div className="mt-1.5 h-1 rounded-full bg-cyber-300 overflow-hidden">
                    <div className="h-full bg-shinobi-green transition-all" style={{ width: `${p.pct}%` }} />
                  </div>
                )}
              </div>
              {i < phases.length - 1 && <ChevronRight className="w-4 h-4 text-gray-700 flex-shrink-0" />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SortHeader({ label, sortKey, sort, onSort, align = 'left' }) {
  const active = sort.key === sortKey;
  const Icon = !active ? ChevronsUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th
      className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider select-none ${align === 'right' ? 'text-right' : 'text-left'}`}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1.5 transition-colors hover:text-slate-800 ${active ? 'text-shinobi-green' : 'text-slate-500'} ${align === 'right' ? 'flex-row-reverse' : ''}`}
        title={`Sort by ${label.toLowerCase()}`}
      >
        {label}
        <Icon className={`w-3.5 h-3.5 ${active ? 'opacity-100' : 'opacity-40'}`} />
      </button>
    </th>
  );
}

function ServicePills({ services, count }) {
  if (!services || services.length === 0) {
    return <span className="text-gray-600 text-xs">—</span>;
  }
  const shown = services.slice(0, PILL_LIMIT);
  const overflow = Math.max(0, (count || services.length) - shown.length);
  return (
    <div className="flex flex-wrap items-center gap-1.5 max-w-md">
      {shown.map((s, i) => (
        <span
          key={`${s.port}/${s.protocol}/${i}`}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-mono whitespace-nowrap bg-wasabi-100 text-wasabi-700 border border-shinobi-green/25"
        >
          {s.port}/{s.protocol}
          {s.product && (
            <span className="font-sans normal-case text-slate-500">{s.product}</span>
          )}
        </span>
      ))}
      {overflow > 0 && (
        <span
          className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-mono whitespace-nowrap bg-cyber-200/50 text-slate-500 border border-slate-200"
          title={`${overflow} more service${overflow === 1 ? '' : 's'}`}
        >
          +{overflow}
        </span>
      )}
    </div>
  );
}

function AssetsEmptyState() {
  return (
    <div className="p-10 text-center">
      <Network className="w-12 h-12 mx-auto mb-3 text-gray-600" />
      <p className="text-slate-700 text-sm mb-1">No assets discovered yet for this vault.</p>
      <p className="text-gray-500 text-xs">
        The monitor beacon runs ARP discovery + fingerprint and populates this table
        within one cycle (default 30 min, first cycle fires on start).
      </p>
    </div>
  );
}

function AssetDrawer({ assetId, onClose }) {
  const { isOperator } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showOverride, setShowOverride] = useState(false);
  const reqRef = useRef(0);

  const fetchAsset = useCallback(async () => {
    const token = ++reqRef.current;
    setLoading(true);
    try {
      const resp = await monitorAPI.getAsset(assetId);
      if (reqRef.current !== token) return; // superseded by a newer request or unmount
      setData(resp.data);
      setError(null);
    } catch (err) {
      if (reqRef.current !== token) return;
      setError(err?.response?.data?.detail || err.message);
    } finally {
      if (reqRef.current === token) setLoading(false);
    }
  }, [assetId]);

  useEffect(() => {
    fetchAsset();
    return () => { reqRef.current += 1; }; // invalidate any in-flight fetch on unmount/asset-switch
  }, [fetchAsset]);

  const openFindings = useMemo(() => {
    if (!data) return [];
    return data.findings
      .filter((f) => !f.resolved_at)
      .sort((a, b) => {
        if (!!b.kev !== !!a.kev) return b.kev ? 1 : -1;
        const ep = (b.epss_score || 0) - (a.epss_score || 0);
        if (ep) return ep;
        return (SEVERITY_ORDER[a.severity] ?? 9) - (SEVERITY_ORDER[b.severity] ?? 9);
      });
  }, [data]);

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40" onClick={onClose} />
      <aside className="fixed top-0 right-0 bottom-0 w-full max-w-2xl bg-cyber-50 border-l border-slate-200 z-50 overflow-y-auto">
        <div className="sticky top-0 bg-cyber-50 border-b border-slate-200 px-5 py-4 flex items-center justify-between z-10">
          <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <Server className="w-4 h-4 text-shinobi-green" />
            {loading ? 'Loading...' : data?.ip}
            {data?.hostname && (
              <span className="text-sm text-slate-500 font-normal ml-1">({data.hostname})</span>
            )}
          </h2>
          <div className="flex items-center gap-3">
            {isOperator && data && (
              <button
                onClick={() => setShowOverride((v) => !v)}
                className="text-xs px-2.5 py-1 rounded border border-slate-200 text-slate-500 hover:text-shinobi-green hover:border-shinobi-green/40 transition-colors"
                title="Override EOL status"
              >
                EOL override
              </button>
            )}
            <button
              onClick={onClose}
              className="text-slate-500 hover:text-slate-900 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-5">
          {loading ? (
            <div className="text-slate-500 flex items-center gap-2 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading details...
            </div>
          ) : error ? (
            <div className="text-red-600 text-sm">Error: {error}</div>
          ) : data ? (
            <>
              {showOverride && (
                <EolOverrideForm
                  assetId={assetId}
                  current={data}
                  onClose={() => setShowOverride(false)}
                  onSaved={async () => {
                    setShowOverride(false);
                    await fetchAsset();
                  }}
                />
              )}

              <AIIdentityCard assetId={assetId} initialIdentity={data.ai_identity} />

              {data.eol_status && data.eol_status !== 'ok' && data.eol_status !== 'unknown' && (
                <div className={`rounded-lg border-l-4 p-3 flex gap-2.5 ${data.eol_status === 'eol' ? 'bg-red-50 border-red-400 border border-red-200' : 'bg-amber-50 border-amber-400 border border-amber-200'}`}>
                  <AlertTriangle className={`w-4 h-4 mt-0.5 shrink-0 ${data.eol_status === 'eol' ? 'text-red-600' : 'text-amber-600'}`} />
                  <div>
                    <div className={`text-sm font-semibold ${data.eol_status === 'eol' ? 'text-red-700' : 'text-amber-700'}`}>
                      {data.eol_status === 'eol' ? 'End-of-Life OS' : 'Approaching End-of-Life'}
                      {data.os_name ? `: ${data.os_name}${data.os_version ? ' ' + data.os_version : ''}` : ''}
                    </div>
                    <div className="text-xs text-slate-600 mt-0.5">
                      {data.eol_status === 'eol' ? 'Support ended' : 'Support ends'} {data.eol_date || 'unknown date'}. No further security patches.
                    </div>
                  </div>
                </div>
              )}

              {(data.multi_homed || data.ai_identity?.exposure) && (
                <div className="rounded-lg border border-dashed border-amber-300 bg-amber-50 p-3 flex gap-2.5">
                  <Network className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
                  <div className="space-y-1">
                    <div className="text-sm font-semibold text-amber-700">Exposure & pivotability</div>
                    {data.ai_identity?.exposure && (
                      <p className="text-xs text-slate-600">{data.ai_identity.exposure}</p>
                    )}
                    {data.multi_homed && (
                      <p className="text-xs text-amber-700 font-medium">
                        Lateral-movement pivot: bridges {(data.connected_subnets || []).filter((s) => s.includes('/')).length} segments.
                      </p>
                    )}
                  </div>
                </div>
              )}

              <ReachabilitySection reach={data.reachability} />

              {data.connected_subnets?.length > 0 && (
                <section>
                  <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                    <Hash className="w-3.5 h-3.5" /> Connected subnets
                  </h3>
                  <ul className="bg-cyber-200/30 rounded border border-slate-200 divide-y divide-slate-200 text-sm">
                    {data.connected_subnets.map((s) => (
                      <li key={s} className="px-3 py-1.5 font-mono text-slate-700">
                        <span>{s}{!s.includes('/') && <span className="text-slate-400 ml-1">(gateway)</span>}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              <section>
                <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                  <Hash className="w-3.5 h-3.5" /> Asset
                </h3>
                <dl className="bg-cyber-200/30 rounded border border-slate-200 divide-y divide-slate-200 text-sm">
                  <DescRow label="IP" value={<span className="font-mono">{data.ip}</span>} />
                  <DescRow label="MAC" value={<span className="font-mono">{data.mac || '—'}</span>} />
                  <DescRow label="Hostname" value={data.hostname || '—'} />
                  <DescRow label="OS" value={data.os_name ? `${data.os_name}${data.os_version ? ' ' + data.os_version : ''}` : '—'} />
                  <DescRow label="EOL" value={<EolCell status={data.eol_status} date={data.eol_date} />} />
                  <DescRow label="Beacon" value={<span className="font-mono text-xs">{data.beacon_id}</span>} />
                  <DescRow label="First seen" value={formatAbsoluteDate(data.first_seen_at)} />
                  <DescRow label="Last seen" value={formatAbsoluteDate(data.last_seen_at)} />
                </dl>
              </section>

              <section>
                <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-2">
                  Services ({data.services.length})
                </h3>
                {data.services.length === 0 ? (
                  <p className="text-xs text-gray-500">No services fingerprinted yet.</p>
                ) : (
                  <div className="bg-cyber-200/30 rounded border border-slate-200 divide-y divide-slate-200">
                    {data.services.map((s) => (
                      <div key={s.id} className="px-3 py-2 flex items-center gap-3 text-sm">
                        <span className="font-mono text-slate-900">{s.port}/{s.protocol}</span>
                        <span className="text-slate-700 flex-1 truncate">
                          {s.product || '?'}{s.version ? ` ${s.version}` : ''}
                        </span>
                        <span className="text-xs text-gray-500" title={formatAbsoluteDate(s.last_seen_at)}>
                          {formatRelativeTime(s.last_seen_at)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section>
                <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" /> Open findings ({openFindings.length})
                </h3>
                {openFindings.length === 0 ? (
                  <p className="text-xs text-gray-500">No open findings.</p>
                ) : (
                  <div className="space-y-1.5">
                    {openFindings.map((f) => (
                      <div key={f.id} className="bg-cyber-200/30 rounded border border-slate-200 p-3">
                        <div className="flex items-start gap-2 mb-1">
                          <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded ${SEVERITY_BADGE[f.severity] || SEVERITY_BADGE.info}`}>
                            {f.severity}
                          </span>
                          <span className={`px-2 py-0.5 text-[10px] font-mono rounded ${SOURCE_BADGE[f.source] || ''}`}>
                            {f.source}
                          </span>
                          {f.kev && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase rounded bg-red-100 text-red-700 border border-red-300" title="CISA Known Exploited Vulnerability">
                              <Flame className="w-3 h-3" /> KEV
                            </span>
                          )}
                          {f.epss_score > 0 && (
                            <span className="px-2 py-0.5 text-[10px] font-mono rounded bg-purple-100 text-purple-700 border border-purple-300" title="FIRST.org EPSS exploit probability">
                              EPSS {Math.round(f.epss_score * 100)}%
                            </span>
                          )}
                          <span className="text-xs font-mono text-gray-500 ml-auto">
                            {f.cve_id || f.template_id || ''}
                          </span>
                        </div>
                        <div className="text-sm text-slate-800">{f.title}</div>
                        {f.description && (
                          <div className="text-xs text-slate-500 mt-1 line-clamp-2">{f.description}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </>
          ) : null}
        </div>
      </aside>
    </>
  );
}

const EOL_OVERRIDE_OPTIONS = [
  { label: 'OK (supported)', value: 'ok' },
  { label: 'Approaching EOL', value: 'approaching' },
  { label: 'End-of-Life', value: 'eol' },
  { label: 'Unknown', value: 'unknown' },
];

// Operator-only inline form to manually override an asset's derived EOL
// status, e.g. when the fingerprint mis-detects the OS or a vendor extends
// support beyond the published end-of-life date.
function EolOverrideForm({ assetId, current, onClose, onSaved }) {
  const [status, setStatus] = useState(current?.eol_status || 'unknown');
  const [date, setDate] = useState(current?.eol_date || '');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await monitorAPI.setAssetEolOverride(assetId, { status, date: date || null, note: note || null });
      await onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-shinobi-purple/30 bg-shinobi-purple/5 p-3 space-y-2.5">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold text-slate-800 uppercase tracking-wider">EOL override</h3>
        <button onClick={onClose} className="text-slate-500 hover:text-slate-800 transition-colors">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="flex flex-wrap items-end gap-2.5">
        <div>
          <label className="text-[11px] text-slate-500 mb-1 block">Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="bg-cyber-300 border border-slate-200 rounded px-2.5 py-1.5 text-sm text-slate-900 focus:outline-none focus:border-shinobi-purple"
          >
            {EOL_OVERRIDE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[11px] text-slate-500 mb-1 block">EOL date</label>
          <input
            type="date"
            value={date || ''}
            onChange={(e) => setDate(e.target.value)}
            className="bg-cyber-300 border border-slate-200 rounded px-2.5 py-1.5 text-sm text-slate-900 focus:outline-none focus:border-shinobi-purple"
          />
        </div>
        <div className="flex-1 min-w-[160px]">
          <label className="text-[11px] text-slate-500 mb-1 block">Note (optional)</label>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Reason for override"
            className="w-full bg-cyber-300 border border-slate-200 rounded px-2.5 py-1.5 text-sm text-slate-900 focus:outline-none focus:border-shinobi-purple"
          />
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="px-3 py-1.5 text-xs rounded bg-shinobi-purple/15 text-shinobi-purple border border-shinobi-purple/40 hover:bg-shinobi-purple/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving…' : 'Save override'}
        </button>
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}

const CRIT_BADGE = {
  critical: 'bg-red-100 text-red-700 border border-red-300',
  high: 'bg-orange-100 text-orange-700 border border-orange-300',
  medium: 'bg-yellow-100 text-yellow-700 border border-yellow-300',
  low: 'bg-blue-100 text-blue-700 border border-blue-300',
};

// Per-asset AI identity + posture. Self-contained: triggers the identify task,
// then polls the asset until the cached identity appears / refreshes.
function AIIdentityCard({ assetId, initialIdentity }) {
  const { isOperator } = useAuth();
  const [identity, setIdentity] = useState(initialIdentity || null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setIdentity(initialIdentity || null);
  }, [initialIdentity, assetId]);

  const run = async (force) => {
    setBusy(true);
    setError(null);
    const prevGen = identity?.generated_at || null;
    try {
      await monitorAPI.identifyAsset(assetId, force);
      for (let i = 0; i < 20; i += 1) {
        await new Promise((r) => setTimeout(r, 3000));
        const resp = await monitorAPI.getAsset(assetId);
        const ident = resp.data?.ai_identity;
        if (ident && ident.generated_at !== prevGen) {
          setIdentity(ident);
          setBusy(false);
          return;
        }
      }
      setError('Still working. Check back in a moment.');
      setBusy(false);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
      setBusy(false);
    }
  };

  const crit = (identity?.criticality || '').toLowerCase();

  return (
    <section>
      <div className="rounded-xl border border-shinobi-purple/30 bg-gradient-to-b from-shinobi-purple/5 to-transparent overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-shinobi-purple/20">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg border border-shinobi-purple/40 bg-shinobi-purple/10 flex items-center justify-center text-shinobi-purple">
              <Brain className="w-4 h-4" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
                Wasabi Shinobi AI
              </div>
              <h3 className="text-sm font-semibold text-slate-900">Identity &amp; Posture</h3>
            </div>
          </div>
          {identity && !busy && isOperator && (
            <button
              onClick={() => run(true)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded border border-shinobi-purple/40 bg-shinobi-purple/10 text-shinobi-purple hover:bg-shinobi-purple/20 transition-colors"
              title="Regenerate"
            >
              <RefreshCw className="w-3 h-3" /> Regenerate
            </button>
          )}
        </div>

        <div className="px-4 py-4">
          {busy ? (
            <div className="text-center py-5">
              <div className="relative inline-flex items-center justify-center mb-2">
                <span className="absolute inline-flex w-10 h-10 rounded-full bg-shinobi-purple/20 animate-ping" />
                <span className="relative inline-flex w-9 h-9 rounded-full bg-shinobi-purple/15 border border-shinobi-purple/40 items-center justify-center">
                  <Sparkles className="w-4 h-4 text-shinobi-purple" />
                </span>
              </div>
              <div className="flex items-center justify-center gap-2 text-sm text-slate-800">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-shinobi-purple" />
                Wasabi Shinobi AI is profiling this host
              </div>
              <p className="text-xs text-slate-500 mt-1">Reading the service fingerprint. Usually 5-15 seconds.</p>
            </div>
          ) : !identity ? (
            <div className="text-center py-4">
              <p className="text-sm text-slate-500 mb-3">
                {isOperator
                  ? "Let Wasabi Shinobi AI infer this host's role, criticality, and hardening posture from its services."
                  : 'No AI identity generated for this host yet.'}
              </p>
              {isOperator && (
                <button
                  onClick={() => run(false)}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-shinobi-purple/15 text-shinobi-purple border border-shinobi-purple/40 hover:bg-shinobi-purple/25 transition-colors"
                >
                  <Sparkles className="w-4 h-4" /> Identify with AI
                </button>
              )}
              {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-base font-semibold text-slate-900">{identity.role || 'Unknown device'}</span>
                {crit && (
                  <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded ${CRIT_BADGE[crit] || CRIT_BADGE.low}`}>
                    {crit}
                  </span>
                )}
                {identity.stale && (
                  <span className="px-2 py-0.5 text-[10px] rounded bg-yellow-100 text-yellow-700 border border-yellow-300">
                    fingerprint changed
                  </span>
                )}
              </div>
              {identity.function && <p className="text-sm text-slate-700">{identity.function}</p>}
              {identity.exposure && (
                <p className="text-xs text-slate-500"><span className="text-slate-500">Exposure: </span>{identity.exposure}</p>
              )}
              {identity.posture_summary && (
                <p className="text-sm text-slate-700 leading-relaxed">{identity.posture_summary}</p>
              )}
              {Array.isArray(identity.hardening) && identity.hardening.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Recommended hardening</div>
                  <ul className="space-y-1">
                    {identity.hardening.map((h, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                        <ShieldCheck className="w-3.5 h-3.5 text-shinobi-green mt-0.5 shrink-0" />
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {identity.generated_at && (
                <p className="text-[11px] text-slate-600 pt-1">
                  Wasabi Shinobi AI · generated {formatRelativeTime(identity.generated_at)}
                </p>
              )}
              {error && <p className="text-xs text-red-600">{error}</p>}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

const REACH_TONE = {
  open: 'bg-red-50 text-red-700 border-red-200',
  filtered: 'bg-slate-100 text-slate-600 border-slate-200',
  closed: 'bg-slate-50 text-slate-400 border-slate-200',
};

// Measured paths into this host, grouped by port and labelled per vantage
// point. An open port here means a beacon actually completed a TCP handshake
// from where it sits, which is a stronger claim than the service scan makes.
function ReachabilitySection({ reach }) {
  const grouped = useMemo(() => {
    const paths = reach?.paths || [];
    const byPort = new Map();
    for (const p of paths) {
      const key = `${p.port}/${p.protocol || 'tcp'}`;
      if (!byPort.has(key)) byPort.set(key, { key, port: p.port, vantages: [] });
      byPort.get(key).vantages.push(p);
    }
    const disputed = new Set(reach?.disputed_ports || []);
    const rows = [...byPort.values()].map((r) => ({
      ...r,
      disputed: disputed.has(r.port),
      reachable: r.vantages.some((v) => v.state === 'open'),
    }));
    rows.sort((a, b) => {
      if (a.disputed !== b.disputed) return a.disputed ? -1 : 1;
      if (a.reachable !== b.reachable) return a.reachable ? -1 : 1;
      return a.port - b.port;
    });
    return {
      shown: rows.filter((r) => r.reachable),
      unreachable: rows.filter((r) => !r.reachable).length,
    };
  }, [reach]);

  if (!reach) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 flex gap-2.5">
        <Radar className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" />
        <div>
          <div className="text-sm font-semibold text-slate-700">Reachability not measured</div>
          <p className="text-xs text-slate-500 mt-0.5">
            No beacon has tried to open a connection to this host, so what it accepts from
            elsewhere on the network is unknown rather than closed. Run Probe reachability on the
            vault to measure it.
          </p>
        </div>
      </div>
    );
  }

  return (
    <section>
      <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
        <Radar className="w-3.5 h-3.5" /> Measured reachability
      </h3>
      {reach.disputed_ports?.length > 0 && (
        <p className="text-xs text-amber-700 mb-2">
          Port{reach.disputed_ports.length === 1 ? '' : 's'} {reach.disputed_ports.join(', ')}{' '}
          {reach.disputed_ports.length === 1 ? 'answers' : 'answer'} one beacon and refuse
          {reach.disputed_ports.length === 1 ? 's' : ''} another. Something treats those two sources
          differently: a firewall on this host, or a filter on the path from one of them. If both
          beacons sit in this host's own segment there is no path in between, so it is the host.
        </p>
      )}
      {grouped.shown.length === 0 ? (
        <p className="text-xs text-slate-500">
          Every probed port refused the connection. {grouped.unreachable} port
          {grouped.unreachable === 1 ? '' : 's'} tested.
        </p>
      ) : (
        <ul className="bg-cyber-200/30 rounded border border-slate-200 divide-y divide-slate-200 text-sm">
          {grouped.shown.map((r) => (
            <li key={r.key} className={`px-3 py-2 flex items-center gap-2 flex-wrap ${r.disputed ? 'bg-amber-50/60' : ''}`}>
              <span className="font-mono text-slate-700 w-20 shrink-0">{r.key}</span>
              {r.vantages.map((v, i) => (
                <span
                  key={i}
                  className={`text-[11px] px-2 py-0.5 rounded-full border ${REACH_TONE[v.state] || REACH_TONE.filtered}`}
                  title={v.checked_at ? `checked ${new Date(v.checked_at).toLocaleString()}` : undefined}
                >
                  {v.src_label || v.src_ip || 'unknown beacon'}: {v.state}
                </span>
              ))}
            </li>
          ))}
        </ul>
      )}
      {grouped.shown.length > 0 && grouped.unreachable > 0 && (
        <p className="text-[11px] text-slate-400 mt-1.5">
          {grouped.unreachable} further port{grouped.unreachable === 1 ? '' : 's'} tested, none reachable.
        </p>
      )}
    </section>
  );
}

function DescRow({ label, value }) {
  return (
    <div className="flex">
      <dt className="px-3 py-2 w-32 text-xs text-slate-500 bg-cyber-200/20">{label}</dt>
      <dd className="px-3 py-2 flex-1 text-slate-800">{value}</dd>
    </div>
  );
}

function epssPct(v) {
  const n = Number(v) || 0;
  if (n <= 0) return null;
  return `${Math.round(n * 100)}%`;
}

// Wasabi Shinobi AI "fix these first" panel for a vault. Blends CISA KEV +
// FIRST.org EPSS + CVSS + exposure into a ranked, narrated remediation list.
function AIRiskTriage({ vaultKey, vaultLabel }) {
  const { isOperator } = useAuth();
  const [triage, setTriage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [collapsed, setCollapsed] = useState(true);

  const load = useCallback(async () => {
    try {
      const resp = await monitorAPI.getVaultTriage(vaultKey);
      setTriage(resp.data);
      setError(null);
      setGenerating(resp.data.status === 'generating');
      return resp.data;
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [vaultKey]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  useEffect(() => {
    if (!generating) return undefined;
    const id = setInterval(async () => {
      const d = await load();
      if (d && d.status !== 'generating') clearInterval(id);
    }, 4000);
    return () => clearInterval(id);
  }, [generating, load]);

  const generate = async () => {
    setGenerating(true);
    setCollapsed(false); // expand so the operator can watch progress + result
    setError(null);
    try {
      await monitorAPI.generateVaultTriage(vaultKey);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
      setGenerating(false);
    }
  };

  const status = triage?.status;
  const items = triage?.items || [];
  const openCount = triage?.open_finding_count ?? 0;
  const hasResult = status === 'ok' && items.length > 0;
  // Wasabian is read-only: they can view a cached triage but not (re)generate it.
  const canGenerate = isOperator && !generating && openCount > 0;

  return (
    <div className="rounded-xl border border-shinobi-green/25 bg-gradient-to-b from-shinobi-green/5 to-transparent overflow-hidden">
      <div
        role="button"
        tabIndex={0}
        onClick={() => setCollapsed((c) => !c)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setCollapsed((c) => !c);
          }
        }}
        aria-expanded={!collapsed}
        className={`flex items-center justify-between px-4 py-3 cursor-pointer select-none group ${collapsed ? '' : 'border-b border-wasabi-300'}`}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-9 h-9 rounded-lg border border-wasabi-300 bg-wasabi-100 flex items-center justify-center text-shinobi-green shrink-0">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
              Wasabi Shinobi AI
            </div>
            <h3 className="text-sm font-semibold text-slate-900 group-hover:text-shinobi-green transition-colors truncate">
              Risk Triage — fix these first
            </h3>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {hasResult && (
            <span className="hidden sm:inline-flex items-center text-[11px] px-2 py-0.5 rounded-full bg-wasabi-100 text-wasabi-700 border border-wasabi-300">
              {items.length} ranked
            </span>
          )}
          {generating && (
            <Loader2 className="w-3.5 h-3.5 animate-spin text-shinobi-green" />
          )}
          {canGenerate && (
            <button
              onClick={(e) => { e.stopPropagation(); generate(); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded border border-wasabi-300 bg-wasabi-100 text-wasabi-700 hover:bg-wasabi-100 transition-colors"
            >
              {hasResult ? <RefreshCw className="w-3.5 h-3.5" /> : <Sparkles className="w-3.5 h-3.5" />}
              {hasResult ? 'Refresh' : 'Generate'}
            </button>
          )}
          <ChevronDown
            className={`w-4 h-4 text-slate-500 group-hover:text-shinobi-green transition-transform duration-300 ${collapsed ? '' : 'rotate-180'}`}
          />
        </div>
      </div>

      <div
        className={`grid transition-all duration-300 ease-in-out ${
          collapsed ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100'
        }`}
      >
        <div className="overflow-hidden">
          <div className="px-4 py-4">
            {loading ? (
          <div className="text-slate-500 flex items-center gap-2 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading triage…
          </div>
        ) : generating ? (
          <div className="text-center py-6">
            <div className="relative inline-flex items-center justify-center mb-2">
              <span className="absolute inline-flex w-12 h-12 rounded-full bg-wasabi-100 animate-ping" />
              <span className="relative inline-flex w-11 h-11 rounded-full bg-wasabi-100 border border-wasabi-300 items-center justify-center">
                <Sparkles className="w-5 h-5 text-shinobi-green" />
              </span>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-slate-800">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-shinobi-green" />
              Wasabi Shinobi AI is prioritising this vault
            </div>
            <p className="text-xs text-slate-500 mt-1">Scoring KEV + EPSS + severity + exposure across {openCount} findings.</p>
          </div>
        ) : status === 'error' ? (
          <div className="text-sm">
            <p className="text-red-600 mb-2">Triage failed: {triage?.error || 'unknown error'}</p>
            {canGenerate && (
              <button onClick={generate} className="text-shinobi-green hover:underline text-xs">Try again</button>
            )}
          </div>
        ) : openCount === 0 ? (
          <p className="text-sm text-slate-500">No open findings in this vault. Nothing to prioritise — nice.</p>
        ) : !hasResult ? (
          <div className="text-center py-4">
            <p className="text-sm text-slate-500 mb-3">
              {openCount} open finding{openCount === 1 ? '' : 's'}.{' '}
              {isOperator
                ? 'Let Wasabi Shinobi AI rank what to fix first by real-world exploitability (KEV + EPSS), not just CVSS.'
                : 'No AI risk triage has been generated for this vault yet.'}
            </p>
            {isOperator && (
              <button
                onClick={generate}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-wasabi-100 text-wasabi-700 border border-wasabi-300 hover:bg-shinobi-green/25 transition-colors"
              >
                <Sparkles className="w-4 h-4" /> Generate AI triage
              </button>
            )}
            {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
          </div>
        ) : (
          <div className="space-y-3">
            {triage.stale && (
              <div className="text-xs rounded border border-yellow-300 bg-yellow-100 text-yellow-700 px-3 py-1.5">
                Findings changed since this triage ({triage.finding_count} → {openCount}). Refresh for the latest.
              </div>
            )}
            {triage.summary && (
              <p className="text-sm text-slate-700 leading-relaxed">{triage.summary}</p>
            )}
            <ol className="space-y-2">
              {items.map((it) => (
                <li key={it.finding_id} className="rounded-lg border border-slate-200 bg-cyber-200/30 p-3">
                  <div className="flex items-start gap-2.5">
                    <span className="w-6 h-6 rounded-full bg-wasabi-100 text-wasabi-700 border border-wasabi-300 flex items-center justify-center text-xs font-bold shrink-0">
                      {it.rank}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap mb-1">
                        <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded ${SEVERITY_BADGE[it.severity] || SEVERITY_BADGE.info}`}>
                          {it.severity}
                        </span>
                        {it.kev && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-red-100 text-red-700 border border-red-300" title="CISA Known Exploited Vulnerability">
                            <Flame className="w-3 h-3" /> KEV
                          </span>
                        )}
                        {epssPct(it.epss_score) && (
                          <span className="px-2 py-0.5 text-[10px] font-mono rounded bg-purple-100 text-purple-700 border border-purple-300" title="FIRST.org EPSS exploit probability">
                            EPSS {epssPct(it.epss_score)}
                          </span>
                        )}
                        <span className="text-[11px] font-mono text-gray-500">
                          {it.cve_id || it.template_id || ''}
                        </span>
                        {it.asset_ip && (
                          <span className="text-[11px] font-mono text-gray-500 ml-auto">{it.asset_ip}</span>
                        )}
                      </div>
                      <div className="text-sm text-slate-800">{it.title}</div>
                      {it.reason && <div className="text-xs text-slate-500 mt-1">{it.reason}</div>}
                      {it.action && (
                        <div className="text-xs text-shinobi-green/90 mt-1.5 flex items-start gap-1.5">
                          <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                          <span>{it.action}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
            {triage.generated_at && (
              <p className="text-[11px] text-slate-600">
                Wasabi Shinobi AI · {formatRelativeTime(triage.generated_at)} · ranked top {items.length} of {triage.finding_count}
              </p>
            )}
          </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
